import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from './config';
import AdminSidebar from './AdminSidebar';
import '../css/Reward.css';
import { matchesSearch } from '../utils/textSearch';

const EMPTY_CAMPAIGN = {
  title: '',
  description: '',
  rewardType: 'REWARD',
  pointsCost: 0,
  stock: '',
  imageUrl: '',
  isActive: true,
  startsAt: '',
  endsAt: ''
};

const EMPTY_MISSION = {
  title: '',
  icon: '🎯',
  missionType: 'CHECKIN',
  claimMode: 'COUNTED',
  claimFrequency: 'ONCE',
  rewardDelivery: 'COUPON',
  claimAction: 'USER',
  target: 1,
  rewardText: '',
  rewardPoints: 0,
  isActive: true,
  sortOrder: 0,
  startsAt: '',
  endsAt: ''
};

const DEFAULT_REWARD_SETTINGS = {
  resultRecorderPoints: 3,
  matchParticipantPoints: 2,
  matchCompletedPoints: 5
};

function AdminRewards() {
  const navigate = useNavigate();

  const actor = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);

  const [activeTab, setActiveTab] = useState('missions');

  // Campaign
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState(EMPTY_CAMPAIGN);
  const [editingId, setEditingId] = useState(null);

  // Mission
  const [missions, setMissions] = useState([]);
  const [missionForm, setMissionForm] = useState(EMPTY_MISSION);
  const [editingMissionId, setEditingMissionId] = useState(null);

  // Message
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [rewardSettings, setRewardSettings] = useState(DEFAULT_REWARD_SETTINGS);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [reports, setReports] = useState({ stats: {}, pointUsers: [], coupons: [], usageLog: [] });
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportSearch, setReportSearch] = useState('');
  const [couponStatus, setCouponStatus] = useState('ALL');

  // Point manual
  const [pointForm, setPointForm] = useState({
    userId: '',
    amount: '',
    note: ''
  });

  // =====================================================
  // FETCH
  // =====================================================

  const authFetch = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        ...(options.headers || {})
      }
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || 'ทำรายการไม่สำเร็จ');
    }

    return data;
  }, []);

  // =====================================================
  // LOAD
  // =====================================================

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const [campaignData, missionData, settingsData] = await Promise.all([
        authFetch('/api/rewards/admin/campaigns'),
        authFetch('/api/rewards/admin/missions'),
        authFetch('/api/rewards/admin/settings')
      ]);

      setCampaigns(campaignData.campaigns || []);
      setMissions(missionData.missions || []);
      setRewardSettings(settingsData.settings || DEFAULT_REWARD_SETTINGS);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const loadReports = useCallback(async () => {
    try {
      setReportsLoading(true);
      const data = await authFetch('/api/rewards/admin/reports');
      setReports({
        stats: data.stats || {},
        pointUsers: data.pointUsers || [],
        coupons: data.coupons || [],
        usageLog: data.usageLog || []
      });
    } catch (error) {
      setMessage(error.message);
    } finally {
      setReportsLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (String(actor?.role || '').toLowerCase() !== 'admin') {
      navigate('/', { replace: true });
      return;
    }

    load().catch(error => {
      setMessage(error.message);
    });
  }, [actor?.role, load, navigate]);

  useEffect(() => {
    if (['coupon-log', 'points', 'all-coupons'].includes(activeTab)) {
      loadReports();
    }
  }, [activeTab, loadReports]);

  const normalizedReportSearch = reportSearch.trim();
  const matchesReportSearch = useCallback(item => {
    if (!normalizedReportSearch) return true;
    return [item.user_id, item.user_name, item.name, item.title, item.reward_label, item.coupon_code]
      .some(value => matchesSearch(value, normalizedReportSearch));
  }, [normalizedReportSearch]);

  const visiblePointUsers = useMemo(
    () => reports.pointUsers.filter(matchesReportSearch),
    [reports.pointUsers, matchesReportSearch]
  );
  const visibleCoupons = useMemo(
    () => reports.coupons.filter(item => matchesReportSearch(item) && (couponStatus === 'ALL' || item.status === couponStatus)),
    [reports.coupons, matchesReportSearch, couponStatus]
  );
  const visibleUsageLog = useMemo(
    () => reports.usageLog.filter(matchesReportSearch),
    [reports.usageLog, matchesReportSearch]
  );

  const formatReportDate = value => value
    ? new Date(value).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })
    : '-';

  // =====================================================
  // CAMPAIGN ADD / EDIT
  // =====================================================

  const submitCampaign = async event => {
    event.preventDefault();

    try {
      setMessage('');

      const path = editingId
        ? `/api/rewards/admin/campaigns/${editingId}`
        : '/api/rewards/admin/campaigns';

      const data = await authFetch(path, {
        method: editingId ? 'PATCH' : 'POST',
        body: JSON.stringify(form)
      });

      setMessage(`✅ ${data.message}`);

      setForm(EMPTY_CAMPAIGN);
      setEditingId(null);

      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const editCampaign = item => {
    setEditingId(item.id);

    setForm({
      title: item.title || '',
      description: item.description || '',
      rewardType: item.reward_type || 'REWARD',
      pointsCost: Number(item.points_cost || 0),
      stock: item.stock ?? '',
      imageUrl: item.image_url || '',
      isActive: Boolean(item.is_active),

      startsAt: item.starts_at
        ? String(item.starts_at).slice(0, 16)
        : '',

      endsAt: item.ends_at
        ? String(item.ends_at).slice(0, 16)
        : ''
    });

    setActiveTab('campaigns');

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // =====================================================
  // MISSION ADD / EDIT
  // =====================================================

  const submitMission = async event => {
    event.preventDefault();

    try {
      setMessage('');

      const path = editingMissionId
        ? `/api/rewards/admin/missions/${editingMissionId}`
        : '/api/rewards/admin/missions';

      const data = await authFetch(path, {
        method: editingMissionId ? 'PATCH' : 'POST',

        body: JSON.stringify({
          ...missionForm,

          target: Number(missionForm.target || 1),

          rewardPoints: Number(
            missionForm.rewardPoints || 0
          ),

          sortOrder: Number(
            missionForm.sortOrder || 0
          )
        })
      });

      setMessage(`✅ ${data.message}`);

      setMissionForm(EMPTY_MISSION);
      setEditingMissionId(null);

      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const editMission = mission => {
    setEditingMissionId(mission.id);

    setMissionForm({
      title: mission.title || '',
      icon: mission.icon || '🎯',

      missionType:
        mission.mission_type || 'CHECKIN',

      claimMode:
        mission.claim_mode || 'COUNTED',

      claimFrequency:
        mission.claim_frequency || 'ONCE',

      rewardDelivery:
        mission.reward_delivery || (mission.mission_type === 'LINE_LINK' ? 'POINT' : 'COUPON'),

      claimAction:
        mission.claim_action || 'USER',

      target: Number(
        mission.target || 1
      ),

      rewardText:
        mission.reward_text || '',

      rewardPoints:
        Number(
          mission.reward_points || 0
        ),

      isActive:
        Boolean(
          mission.is_active
        ),

      sortOrder:
        Number(
          mission.sort_order || 0
        ),

      startsAt: mission.starts_at ? String(mission.starts_at).slice(0, 16) : '',
      endsAt: mission.ends_at ? String(mission.ends_at).slice(0, 16) : ''
    });

    setActiveTab('missions');

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // =====================================================
  // DELETE MISSION
  // =====================================================

  const deleteMission = async mission => {
    const confirmed = window.confirm(
      `ต้องการลบภารกิจ “${mission.title}” ใช่หรือไม่?`
    );

    if (!confirmed) return;

    try {
      setMessage('');

      const data = await authFetch(
        `/api/rewards/admin/missions/${mission.id}`,
        {
          method: 'DELETE'
        }
      );

      setMessage(`✅ ${data.message}`);

      if (editingMissionId === mission.id) {
        setEditingMissionId(null);
        setMissionForm(EMPTY_MISSION);
      }

      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  // =====================================================
  // OPEN / CLOSE MISSION
  // =====================================================

  const toggleMission = async mission => {
    try {
      setMessage('');

      const data = await authFetch(
        `/api/rewards/admin/missions/${mission.id}`,
        {
          method: 'PATCH',

          body: JSON.stringify({
            isActive: !Boolean(mission.is_active)
          })
        }
      );

      setMessage(`✅ ${data.message}`);

      await load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  // =====================================================
  // MANUAL POINT
  // =====================================================

  const adjustPoints = async event => {
    event.preventDefault();

    try {
      const data = await authFetch(
        '/api/rewards/admin/points',
        {
          method: 'POST',
          body: JSON.stringify(pointForm)
        }
      );

      setMessage(`✅ ${data.message}`);

      setPointForm({
        userId: '',
        amount: '',
        note: ''
      });
    } catch (error) {
      setMessage(error.message);
    }
  };

  const saveRewardSettings = async event => {
    event.preventDefault();
    try {
      setSettingsSaving(true);
      setMessage('');
      const data = await authFetch('/api/rewards/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({
          resultRecorderPoints: Number(rewardSettings.resultRecorderPoints),
          matchParticipantPoints: Number(rewardSettings.matchParticipantPoints),
          matchCompletedPoints: Number(rewardSettings.matchCompletedPoints)
        })
      });
      setRewardSettings(data.settings || rewardSettings);
      setMessage(`✅ ${data.message}`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  // =====================================================
  // TYPE TEXT
  // =====================================================

  const missionTypeText = type => {
    if (type === 'LINE_LINK') {
      return '🎯 ผูก LINE กับ Player ID';
    }
    if (type === 'CHECKIN') {
      return '📍 นับ Check-in';
    }

    if (type === 'MATCH') {
      return '🏸 นับเกมที่เล่นจบ';
    }

    if (type === 'ADMIN_CONFIRM') {
      return '👕 Admin ยืนยัน';
    }

    if (type === 'REFERRAL') {
      return '🤝 ชวนเพื่อนด้วย QR';
    }

    return type;
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <main className="reward-admin-page admin-shell-with-sidebar">
      <AdminSidebar active="rewards" />

      <header>
        <button
          type="button"
          onClick={() => navigate('/profile')}
        >
          ← Profile
        </button>

        <div>
          <small>ADMIN ONLY</small>

          <h1>🛡 Reward Admin</h1>

          <p>
            จัดการภารกิจ โปรโมชั่น ของรางวัล และ Point ผู้เล่น
          </p>
        </div>
      </header>

      {message && (
        <div
          className={
            message.startsWith('✅')
              ? 'reward-admin-message success'
              : 'reward-admin-message'
          }
        >
          {message}
        </div>
      )}

      {/* ================= TAB ================= */}

      <div className="reward-admin-tabs">

        <button
          type="button"
          className={
            activeTab === 'missions'
              ? 'active'
              : ''
          }
          onClick={() =>
            setActiveTab('missions')
          }
        >
          🎯 ภารกิจ
        </button>

        <button
          type="button"
          className={
            activeTab === 'campaigns'
              ? 'active'
              : ''
          }
          onClick={() =>
            setActiveTab('campaigns')
          }
        >
          🎁 ของรางวัล / โปรโมชัน
        </button>

        <button
          type="button"
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          ⭐ ตั้งค่าแต้มการแข่งขัน
        </button>

        <button type="button" className={activeTab === 'coupon-log' ? 'active' : ''} onClick={() => setActiveTab('coupon-log')}>
          📊 Log ใช้คูปอง
        </button>

        <button type="button" className={activeTab === 'points' ? 'active' : ''} onClick={() => setActiveTab('points')}>
          ⭐ พ้อยของทุกคน
        </button>

        <button type="button" className={activeTab === 'all-coupons' ? 'active' : ''} onClick={() => setActiveTab('all-coupons')}>
          🎟️ คูปองของทุกคน
        </button>

      </div>

      {['coupon-log', 'points', 'all-coupons'].includes(activeTab) && (
        <section className="reward-admin-report">
          <header className="reward-admin-report-head">
            <div>
              <h2>{activeTab === 'coupon-log' ? '📊 สถิติและ Log การใช้คูปอง' : activeTab === 'points' ? '⭐ พ้อยของผู้เล่นทุกคน' : '🎟️ คูปองของผู้เล่นทุกคน'}</h2>
              <p>ข้อมูลส่วนนี้สำหรับ Admin เท่านั้น</p>
            </div>
            <button type="button" onClick={loadReports} disabled={reportsLoading}>↻ {reportsLoading ? 'กำลังโหลด' : 'อัปเดต'}</button>
          </header>

          {activeTab === 'coupon-log' && (
            <div className="reward-report-stats">
              <article><span>คูปองทั้งหมด</span><strong>{Number(reports.stats.total_coupons || 0).toLocaleString()}</strong></article>
              <article><span>ใช้แล้วทั้งหมด</span><strong>{Number(reports.stats.redeemed_coupons || 0).toLocaleString()}</strong></article>
              <article><span>ยังใช้ได้/รอยืนยัน</span><strong>{Number(reports.stats.available_coupons || 0).toLocaleString()}</strong></article>
              <article><span>ใช้วันนี้</span><strong>{Number(reports.stats.redeemed_today || 0).toLocaleString()}</strong></article>
            </div>
          )}

          <div className="reward-report-tools">
            <input value={reportSearch} onChange={event => setReportSearch(event.target.value)} placeholder="ค้นหาชื่อ, Player ID, ชื่อรางวัล หรือรหัสคูปอง" />
            {activeTab === 'all-coupons' && (
              <select value={couponStatus} onChange={event => setCouponStatus(event.target.value)}>
                <option value="ALL">ทุกสถานะ</option>
                <option value="AVAILABLE">พร้อมใช้</option>
                <option value="PENDING">รอยืนยัน</option>
                <option value="REDEEMED">ใช้แล้ว</option>
              </select>
            )}
          </div>

          {reportsLoading && <div className="reward-empty">กำลังโหลดข้อมูล…</div>}

          {!reportsLoading && activeTab === 'coupon-log' && (
            <div className="reward-report-table-wrap"><table className="reward-report-table">
              <thead><tr><th>วันที่ใช้</th><th>ผู้เล่น</th><th>คูปอง/รางวัล</th><th>ประเภท</th><th>ผู้ยืนยัน</th></tr></thead>
              <tbody>{visibleUsageLog.map(item => <tr key={`${item.coupon_type}-${item.id}`}><td>{formatReportDate(item.redeemed_at)}</td><td><b>{item.user_name}</b><small>ID {item.user_id}</small></td><td>{item.title}</td><td>{item.coupon_type === 'MISSION' ? 'ภารกิจ' : 'แลกพ้อย'}</td><td>{item.redeemed_by_name || 'Admin'}</td></tr>)}</tbody>
            </table>{visibleUsageLog.length === 0 && <div className="reward-empty">ยังไม่มีประวัติการใช้คูปอง</div>}</div>
          )}

          {!reportsLoading && activeTab === 'points' && (
            <div className="reward-report-table-wrap"><table className="reward-report-table">
              <thead><tr><th>อันดับ</th><th>ผู้เล่น</th><th>ระดับ</th><th>พ้อยคงเหลือ</th><th>รายการพ้อย</th><th>อัปเดตล่าสุด</th></tr></thead>
              <tbody>{visiblePointUsers.map((item, index) => <tr key={item.user_id}><td>#{index + 1}</td><td><b>{item.name}</b><small>ID {item.user_id}</small></td><td>{item.level_rank || '-'}</td><td><strong className="reward-point-value">{Number(item.reward_points || 0).toLocaleString()} คะแนน</strong></td><td>{Number(item.transaction_count || 0).toLocaleString()}</td><td>{formatReportDate(item.last_point_at)}</td></tr>)}</tbody>
            </table>{visiblePointUsers.length === 0 && <div className="reward-empty">ไม่พบผู้เล่น</div>}</div>
          )}

          {!reportsLoading && activeTab === 'all-coupons' && (
            <div className="reward-report-table-wrap"><table className="reward-report-table">
              <thead><tr><th>วันที่ได้รับ</th><th>ผู้เล่น</th><th>คูปอง/รางวัล</th><th>ประเภท</th><th>สถานะ</th><th>วันที่ใช้</th></tr></thead>
              <tbody>{visibleCoupons.map(item => <tr key={`${item.coupon_type}-${item.id}`}><td>{formatReportDate(item.created_at)}</td><td><b>{item.user_name}</b><small>ID {item.user_id}</small></td><td><b>{item.reward_label || item.title}</b><small>{item.coupon_code}</small></td><td>{item.coupon_type === 'MISSION' ? 'ภารกิจ' : 'แลกพ้อย'}</td><td><span className={`reward-coupon-status is-${String(item.status).toLowerCase()}`}>{item.status}</span></td><td>{formatReportDate(item.redeemed_at)}</td></tr>)}</tbody>
            </table>{visibleCoupons.length === 0 && <div className="reward-empty">ไม่พบคูปอง</div>}</div>
          )}
        </section>
      )}

      {activeTab === 'settings' && (
        <section className="reward-match-settings">
          <header>
            <div>
              <h2>⭐ ตั้งค่าแต้มการแข่งขัน</h2>
              <p>ค่าที่บันทึกจะใช้กับรายการใหม่เท่านั้น และแต่ละผู้เล่นรับแต้มจากแมตช์เดิมได้ครั้งเดียว</p>
            </div>
          </header>

          <form onSubmit={saveRewardSettings}>
            <label>
              <span>ผู้กดบันทึกผลการแข่งขัน</span>
              <small>แต้มสำหรับ User ที่กดเลือก T1 WIN, DRAW หรือ T2 WIN</small>
              <input
                type="number"
                min="0"
                max="10000"
                step="1"
                required
                value={rewardSettings.resultRecorderPoints}
                onChange={event => setRewardSettings({ ...rewardSettings, resultRecorderPoints: event.target.value })}
              />
            </label>

            <label>
              <span>ผู้เล่นร่วมแมตช์</span>
              <small>แต้มสำหรับผู้เล่นคนอื่นในแมตช์เมื่อ User บันทึกผล</small>
              <input
                type="number"
                min="0"
                max="10000"
                step="1"
                required
                value={rewardSettings.matchParticipantPoints}
                onChange={event => setRewardSettings({ ...rewardSettings, matchParticipantPoints: event.target.value })}
              />
            </label>

            <label>
              <span>เล่นจบแมตช์</span>
              <small>แต้มพื้นฐานที่ผู้เล่นได้รับหลังสถานะแมตช์เป็น finished</small>
              <input
                type="number"
                min="0"
                max="10000"
                step="1"
                required
                value={rewardSettings.matchCompletedPoints}
                onChange={event => setRewardSettings({ ...rewardSettings, matchCompletedPoints: event.target.value })}
              />
            </label>

            <button type="submit" disabled={settingsSaving}>
              {settingsSaving ? 'กำลังบันทึก…' : '💾 บันทึกการตั้งค่าแต้ม'}
            </button>
          </form>
        </section>
      )}

      {/* =====================================================
          MISSIONS
      ===================================================== */}

      {activeTab === 'missions' && (
        <>

          <section className="reward-admin-layout">

            <form
              className="reward-admin-form"
              onSubmit={submitMission}
            >

              <h2>
                {editingMissionId
                  ? '✏️ แก้ไขภารกิจ'
                  : '➕ เพิ่มภารกิจ'}
              </h2>

              {/* ICON + TITLE */}

              <div className="mission-title-row">

                <input
                  className="mission-icon-input"
                  placeholder="🎯"
                  value={missionForm.icon}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      icon: e.target.value
                    })
                  }
                />

                <input
                  required
                  placeholder="ชื่อภารกิจ เช่น Check-in"
                  value={missionForm.title}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      title: e.target.value
                    })
                  }
                />

              </div>

              {/* TYPE */}

              <label>
                ประเภทภารกิจ

                <select
                  value={missionForm.missionType}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      missionType: e.target.value,
                      ...(e.target.value === 'LINE_LINK'
                        ? { rewardDelivery: 'POINT', claimAction: 'USER', target: 1 }
                        : e.target.value === 'MATCH'
                          ? { claimMode: 'COUNTED', claimFrequency: 'UNLIMITED' }
                        : e.target.value === 'REFERRAL'
                          ? { rewardDelivery: 'COUPON', claimAction: 'USER', claimFrequency: 'UNLIMITED', target: 1, rewardText: missionForm.rewardText || 'ฟรี 1 ชิป' }
                          : {})
                    })
                  }
                >
                  <option value="CHECKIN">
                    📍 นับ Check-in
                  </option>

                  <option value="LINE_LINK">
                    ผูก LINE กับ Player ID
                  </option>

                  <option value="MATCH">
                    🏸 นับเกมที่เล่นจบ
                  </option>

                  <option value="ADMIN_CONFIRM">
                    👕 Admin ยืนยัน
                  </option>

                  <option value="REFERRAL">
                    🤝 ชวนเพื่อนด้วย QR
                  </option>
                </select>
              </label>

              <label>
                วิธีปลดล็อกรางวัล

                <select
                  value={missionForm.claimMode}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      claimMode: e.target.value,
                      target: e.target.value === 'INSTANT' ? 1 : missionForm.target
                    })
                  }
                >
                  <option value="COUNTED">ครบตามจำนวนครั้งที่กำหนด</option>
                  <option value="INSTANT">รับได้ทันที แล้วให้ Admin สแกนยืนยัน</option>
                </select>
              </label>

              <label>
                สิทธิ์การรับรางวัล

                <select
                  value={missionForm.claimFrequency}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      claimFrequency: e.target.value
                    })
                  }
                >
                  <option value="ONCE">รับได้ครั้งเดียว</option>
                  <option value="PER_CHECKIN">รับได้ใหม่ทุกครั้งที่ Check-in Group</option>
                  <option value="UNLIMITED">ทำซ้ำได้ไม่จำกัดจำนวนครั้ง</option>
                </select>
              </label>

              <label>
                รูปแบบรางวัล

                <select
                  value={missionForm.rewardDelivery}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      rewardDelivery: e.target.value,
                      claimAction: e.target.value === 'POINT' ? 'USER' : missionForm.claimAction
                    })
                  }
                >
                  <option value="POINT">เพิ่ม Point เข้าบัญชีเมื่อกดรับ</option>
                  <option value="COUPON">ออกเป็นคูปองของรางวัล</option>
                </select>
              </label>

              {missionForm.rewardDelivery === 'COUPON' && (
                <label>
                  ปุ่มรับรางวัล

                  <select
                    value={missionForm.claimAction}
                    onChange={e =>
                      setMissionForm({
                        ...missionForm,
                        claimAction: e.target.value
                      })
                    }
                  >
                    <option value="USER">แสดงปุ่มให้ผู้เล่นกดรับ</option>
                    <option value="ADMIN">รับที่ Admin — ออกคูปองอัตโนมัติ</option>
                  </select>
                </label>
              )}

              {/* TARGET */}

              {missionForm.claimMode !== 'INSTANT' && (
                <label>
                  จำนวนที่ต้องทำ

                  <input
                    type="number"
                    min="1"
                    required
                    value={missionForm.target}
                    onChange={e =>
                      setMissionForm({
                        ...missionForm,
                        target: e.target.value
                      })
                    }
                  />
                </label>
              )}

              {/* REWARD TEXT */}

              <label>
                รางวัลเมื่อทำสำเร็จ

                <input
                  placeholder="เช่น รับฟรีค่าสนาม 1 ครั้ง"
                  value={missionForm.rewardText}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      rewardText: e.target.value
                    })
                  }
                />
              </label>

              {/* POINT */}

              <label>
                ⭐ Point เมื่อทำภารกิจสำเร็จ

                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="เช่น 50"
                  value={missionForm.rewardPoints}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      rewardPoints: e.target.value
                    })
                  }
                />

                <small>
                  ใส่ 0 ถ้าไม่ต้องการแจก Point
                </small>
              </label>

              {/* SORT */}

              <label>
                ลำดับการแสดง

                <input
                  type="number"
                  min="0"
                  value={missionForm.sortOrder}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      sortOrder: e.target.value
                    })
                  }
                />
              </label>

              <div className="reward-admin-date-grid">
                <label>
                  เริ่มแสดงภารกิจ
                  <input type="datetime-local" value={missionForm.startsAt} onChange={e => setMissionForm({ ...missionForm, startsAt: e.target.value })} />
                  <small>เว้นว่างเพื่อเริ่มทันที</small>
                </label>
                <label>
                  สิ้นสุดภารกิจ
                  <input type="datetime-local" value={missionForm.endsAt} onChange={e => setMissionForm({ ...missionForm, endsAt: e.target.value })} />
                  <small>เมื่อถึงเวลาจะปิดจากหน้า User อัตโนมัติ</small>
                </label>
              </div>

              {/* ACTIVE */}

              <label className="reward-admin-check">

                <input
                  type="checkbox"
                  checked={missionForm.isActive}
                  onChange={e =>
                    setMissionForm({
                      ...missionForm,
                      isActive: e.target.checked
                    })
                  }
                />

                เปิดให้ผู้เล่นเห็น

              </label>

              <button type="submit">
                {editingMissionId
                  ? '💾 บันทึกการแก้ไข'
                  : '＋ เพิ่มภารกิจ'}
              </button>

              {editingMissionId && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setEditingMissionId(null);
                    setMissionForm(EMPTY_MISSION);
                  }}
                >
                  ยกเลิก
                </button>
              )}

            </form>

          </section>

          {/* ================= MISSION LIST ================= */}

          <section className="reward-admin-list">

            <h2>
              ภารกิจทั้งหมด
            </h2>

            {loading && (
              <div className="reward-empty">
                กำลังโหลด...
              </div>
            )}

            {!loading &&
              missions.length === 0 && (
                <div className="reward-empty">
                  ยังไม่มีภารกิจ
                </div>
              )}

            {missions.map(mission => (
              <article
                key={mission.id}
                className="mission-admin-item"
              >

                <div className="mission-admin-main">

                  <div className="mission-admin-icon">
                    {mission.icon || '🎯'}
                  </div>

                  <div>

                    <small>
                      {missionTypeText(
                        mission.mission_type
                      )}
                    </small>

                    <strong>
                      {mission.title}
                    </strong>

                    <span>
                      {mission.claim_mode === 'INSTANT'
                        ? 'รับได้ทันที • Admin สแกนยืนยัน'
                        : `เป้าหมาย ${mission.target} ${
                            mission.mission_type ===
                            'MATCH'
                              ? 'เกม'
                              : 'ครั้ง'
                          }`}
                      {' • '}
                      {mission.claim_frequency === 'PER_CHECKIN'
                        ? 'รับใหม่ได้ทุก Check-in'
                        : mission.claim_frequency === 'UNLIMITED'
                          ? 'ทำซ้ำได้ไม่จำกัด'
                        : 'รับได้ครั้งเดียว'}
                      {' • '}
                      {mission.reward_delivery === 'POINT'
                        ? 'เพิ่ม Point เมื่อกดรับ'
                        : mission.claim_action === 'ADMIN'
                          ? 'ออกคูปองอัตโนมัติ รับที่ Admin'
                          : 'ผู้เล่นกดรับคูปอง'}
                    </span>

                    {mission.reward_text && (
                      <em>
                        🎁 {mission.reward_text}
                      </em>
                    )}

                    {Number(
                      mission.reward_points || 0
                    ) > 0 && (
                      <em>
                        ⭐ +
                        {Number(
                          mission.reward_points
                        ).toLocaleString()}
                        {' '}Point
                      </em>
                    )}

                    {(mission.starts_at || mission.ends_at) && (
                      <em>
                        🕒 {mission.starts_at ? new Date(mission.starts_at).toLocaleString('th-TH') : 'เริ่มทันที'}
                        {' — '}
                        {mission.ends_at ? new Date(mission.ends_at).toLocaleString('th-TH') : 'ไม่กำหนดสิ้นสุด'}
                      </em>
                    )}

                  </div>

                </div>

                <button
                  type="button"
                  className={
                    mission.is_active
                      ? 'mission-status active'
                      : 'mission-status inactive'
                  }
                  onClick={() =>
                    toggleMission(mission)
                  }
                >
                  {mission.is_active
                    ? 'เปิด'
                    : 'ปิด'}
                </button>

                <div className="mission-admin-actions">

                  <button
                    type="button"
                    onClick={() =>
                      editMission(mission)
                    }
                  >
                    ✏️ แก้ไข
                  </button>

                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      deleteMission(mission)
                    }
                  >
                    🗑 ลบ
                  </button>

                </div>

              </article>
            ))}

          </section>

        </>
      )}

      {/* =====================================================
          CAMPAIGNS
      ===================================================== */}

      {activeTab === 'campaigns' && (
        <>

          <section className="reward-admin-layout">

            <form
              className="reward-admin-form"
              onSubmit={submitCampaign}
            >

              <h2>
                {editingId
                  ? '✏️ แก้ไขกิจกรรม'
                  : '➕ เพิ่มกิจกรรม/ของรางวัล'}
              </h2>

              <input
                required
                minLength="2"
                placeholder="ชื่อกิจกรรมหรือของรางวัล"
                value={form.title}
                onChange={e =>
                  setForm({
                    ...form,
                    title: e.target.value
                  })
                }
              />

              <textarea
                placeholder="รายละเอียด"
                value={form.description}
                onChange={e =>
                  setForm({
                    ...form,
                    description: e.target.value
                  })
                }
              />

              <div>

                <select
                  value={form.rewardType}
                  onChange={e =>
                    setForm({
                      ...form,
                      rewardType: e.target.value
                    })
                  }
                >
                  <option value="REWARD">
                    ของรางวัล
                  </option>

                  <option value="PROMOTION">
                    โปรโมชัน
                  </option>
                </select>

                <input
                  type="number"
                  min="0"
                  placeholder="Point ที่ใช้"
                  value={form.pointsCost}
                  onChange={e =>
                    setForm({
                      ...form,
                      pointsCost: e.target.value
                    })
                  }
                />

                <input
                  type="number"
                  min="0"
                  placeholder="จำนวน (ว่าง=ไม่จำกัด)"
                  value={form.stock}
                  onChange={e =>
                    setForm({
                      ...form,
                      stock: e.target.value
                    })
                  }
                />

              </div>

              <input
                placeholder="URL รูปภาพ (ไม่บังคับ)"
                value={form.imageUrl}
                onChange={e =>
                  setForm({
                    ...form,
                    imageUrl: e.target.value
                  })
                }
              />

              <div>

                <label>
                  เริ่ม

                  <input
                    type="datetime-local"
                    value={form.startsAt}
                    onChange={e =>
                      setForm({
                        ...form,
                        startsAt: e.target.value
                      })
                    }
                  />
                </label>

                <label>
                  สิ้นสุด

                  <input
                    type="datetime-local"
                    value={form.endsAt}
                    onChange={e =>
                      setForm({
                        ...form,
                        endsAt: e.target.value
                      })
                    }
                  />
                </label>

              </div>

              <label className="reward-admin-check">

                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e =>
                    setForm({
                      ...form,
                      isActive: e.target.checked
                    })
                  }
                />

                เปิดให้ผู้เล่นเห็น

              </label>

              <button type="submit">
                {editingId
                  ? '💾 บันทึกการแก้ไข'
                  : '＋ เพิ่มกิจกรรม'}
              </button>

              {editingId && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setEditingId(null);
                    setForm(EMPTY_CAMPAIGN);
                  }}
                >
                  ยกเลิก
                </button>
              )}

            </form>

            {/* POINT / REDEEM */}

            <div className="reward-admin-tools">

              <form onSubmit={adjustPoints}>

                <h2>
                  เพิ่ม/ลด Point
                </h2>

                <input
                  required
                  type="number"
                  min="1"
                  placeholder="Player ID"
                  value={pointForm.userId}
                  onChange={e =>
                    setPointForm({
                      ...pointForm,
                      userId: e.target.value
                    })
                  }
                />

                <input
                  required
                  type="number"
                  placeholder="เช่น 20 หรือ -20"
                  value={pointForm.amount}
                  onChange={e =>
                    setPointForm({
                      ...pointForm,
                      amount: e.target.value
                    })
                  }
                />

                <input
                  placeholder="เหตุผล"
                  value={pointForm.note}
                  onChange={e =>
                    setPointForm({
                      ...pointForm,
                      note: e.target.value
                    })
                  }
                />

                <button type="submit">
                  บันทึก Point
                </button>

              </form>

            </div>

          </section>

          {/* CAMPAIGN LIST */}

          <section className="reward-admin-list">

            <h2>
              กิจกรรมและของรางวัลทั้งหมด
            </h2>

            {campaigns.map(item => (
              <article key={item.id}>

                <div>

                  <small>
                    {item.reward_type}
                  </small>

                  <strong>
                    {item.title}
                  </strong>

                  <span>
                    {item.points_cost} Point
                    {' '}• คงเหลือ{' '}
                    {item.stock == null
                      ? 'ไม่จำกัด'
                      : item.stock}
                  </span>

                </div>

                <b
                  className={
                    item.is_active
                      ? 'active'
                      : 'inactive'
                  }
                >
                  {item.is_active
                    ? 'เปิด'
                    : 'ปิด'}
                </b>

                <button
                  type="button"
                  onClick={() =>
                    editCampaign(item)
                  }
                >
                  แก้ไข
                </button>

              </article>
            ))}

          </section>

        </>
      )}

    </main>
  );
}

export default AdminRewards;
